import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/user-provider";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import { CopyIcon, GiftIcon } from "lucide-react";

export function CardReferralDiscounts() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { toast } = useToast();
  const [, copy] = useCopyToClipboard();

  const hasReferralDiscounts =
    user?.referralMonthlyDiscount || user?.referralAnnualDiscount;

  if (!hasReferralDiscounts) {
    return null;
  }

  // Check if same code for both
  const hasSameCode =
    user?.referralMonthlyPromotionCode &&
    user?.referralAnnualPromotionCode &&
    user.referralMonthlyPromotionCode === user.referralAnnualPromotionCode &&
    user.referralMonthlyDiscount === user.referralAnnualDiscount;

  const handleCopy = async (code: string, label: string) => {
    const success = await copy(code);
    if (success) {
      toast({
        variant: "default",
        title: t("referrals.discounts.codeCopied"),
        description: t("referrals.discounts.codeCopiedDescription", {
          code: label,
        }),
      });
    }
  };

  // Determine title based on unified or separate
  const title = hasSameCode
    ? t("referrals.discounts.titleSingular")
    : t("referrals.discounts.titlePlural");

  return (
    <Card className="flex flex-col flex-1 justify-between">
      <CardHeader>
        <CardTitle className="flex gap-2 items-center">
          <GiftIcon className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 justify-between">
        <div className="mb-4 text-sm text-muted-foreground">
          {t("referrals.discounts.description")}
        </div>

        {hasSameCode ? (
          // Unified view: same code for both
          <div className="mt-auto space-y-3">
            <div className="flex gap-2 items-center">
              <Badge
                variant="secondary"
                className="px-3 py-1 text-lg font-bold"
              >
                -{user.referralMonthlyDiscount}%
              </Badge>
              <span className="text-sm text-muted-foreground">
                {t("referrals.discounts.forReferrals")}
              </span>
            </div>
            {user.referralMonthlyPromotionCode && (
              <div className="flex items-center space-x-2 w-full">
                <Input
                  type="text"
                  value={user.referralMonthlyPromotionCode}
                  readOnly
                  className="flex-1"
                />
                <Button
                  type="button"
                  size="sm"
                  className="px-3"
                  onClick={() =>
                    handleCopy(
                      user.referralMonthlyPromotionCode!,
                      user.referralMonthlyPromotionCode!
                    )
                  }
                >
                  <CopyIcon className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          // Separate view: different codes
          <div className="mt-auto space-y-3">
            {user?.referralMonthlyDiscount && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Badge
                    variant="secondary"
                    className="px-3 py-1 text-base font-bold"
                  >
                    -{user.referralMonthlyDiscount}%
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {t("referrals.discounts.monthly")}
                  </span>
                </div>
                {user.referralMonthlyPromotionCode && (
                  <div className="flex items-center space-x-2 w-full">
                    <Input
                      type="text"
                      value={user.referralMonthlyPromotionCode}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="px-3"
                      onClick={() =>
                        handleCopy(
                          user.referralMonthlyPromotionCode!,
                          t("referrals.discounts.monthly")
                        )
                      }
                    >
                      <CopyIcon className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            {user?.referralAnnualDiscount && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Badge
                    variant="secondary"
                    className="px-3 py-1 text-base font-bold"
                  >
                    -{user.referralAnnualDiscount}%
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {t("referrals.discounts.annual")}
                  </span>
                </div>
                {user.referralAnnualPromotionCode && (
                  <div className="flex items-center space-x-2 w-full">
                    <Input
                      type="text"
                      value={user.referralAnnualPromotionCode}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="px-3"
                      onClick={() =>
                        handleCopy(
                          user.referralAnnualPromotionCode!,
                          t("referrals.discounts.annual")
                        )
                      }
                    >
                      <CopyIcon className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
